import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton,
  Tooltip, useToast, Badge, SimpleGrid, Icon, Image,
  Alert, AlertIcon, VStack, Divider, Heading, InputGroup, Input,
  InputRightElement, Select, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalCloseButton, ModalBody, ModalFooter, useDisclosure,
  Tabs, TabList, TabPanels, Tab, TabPanel, Textarea, FormControl,
  FormLabel, Skeleton, Avatar, HStack,
  NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper,
  NumberDecrementStepper, Link, Tag, TagLabel, TagLeftIcon,
  AlertDescription, AlertTitle, useBreakpointValue
} from '@chakra-ui/react';
import {
  FaWallet, FaBoxOpen, FaPrint, FaSyncAlt, FaExclamationCircle,
  FaFilePdf, FaStore, FaTruck, FaCheck, FaTimesCircle, FaShoppingCart,
  FaTrash, FaUser, FaMapMarkerAlt, FaMoneyBillWave, FaQrcode, FaBan, FaCopy,
  FaBuilding, FaIdCard, FaPhone, FaEnvelope, FaCalendarAlt, FaCheckCircle,
  FaClock, FaRoute, FaBox, FaSearch, FaChevronLeft, FaChevronRight,
  FaInfoCircle, FaShieldAlt, FaBolt, FaBarcode, FaFileCode
} from 'react-icons/fa';
import { ViewIcon, TimeIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { api, getVendasML } from '../services/api';
import GeradorEtiquetaMelhorEnvio from "../components/GeradorEtiquetaMelhorEnvio";
import ScrollToTop from "../components/ScrollToTop";
import { useDebounce } from "../hooks/useDebounce";

// ─────────────────────────────────────────────
//  CONSTANTES DE STATUS
// ─────────────────────────────────────────────
const STATUS_MAP = {
  released:    { color: 'green',  label: 'Liberada',      icon: FaCheckCircle },
  received:    { color: 'purple', label: 'Na Agência',    icon: FaStore },
  posted:      { color: 'teal',   label: 'Em Trânsito',   icon: FaTruck },
  delivered:   { color: 'blue',   label: 'Entregue',      icon: FaCheck },
  canceled:    { color: 'red',    label: 'Cancelada',     icon: FaTimesCircle },
  undelivered: { color: 'orange', label: 'Não Entregue',  icon: FaExclamationCircle },
  pending:     { color: 'yellow', label: 'Pendente',      icon: FaClock },
};

const getStatusInfo = (status) =>
  STATUS_MAP[status] || { color: 'gray', label: status || 'Desconhecido', icon: FaInfoCircle };

const formatDate = (dateString) => {
  if (!dateString) return null;
  const d = new Date(dateString);
  return d.toLocaleDateString('pt-BR') + ' · ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const formatCurrency = (value) =>
  parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDoc = (doc) => {
  if (!doc) return '-';
  const d = doc.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
};

const canCancel = (status) => ['pending', 'released', 'paid'].includes(status);

// Helpers específicos do shape do endereço ME: city pode ser objeto {city, state:{state_abbr}} ou string
const getCidade = (end) => {
  if (!end) return '';
  if (typeof end.city === 'string') return end.city;
  return end.city?.city || '';
};
const getUF = (end) => {
  if (!end) return '';
  if (end.state_abbr) return end.state_abbr;
  return end.city?.state?.state_abbr || end.state?.state_abbr || '';
};
const getTelefone = (user) => {
  if (!user) return '';
  if (typeof user.phone === 'string') return user.phone;
  return user.phone?.phone || '';
};
// Saldo em ME vem em accounts[0].balance; também há formato legado `balance` direto
const getSaldoUsuario = (user) => {
  if (!user) return 0;
  if (typeof user.balance === 'number') return user.balance;
  return parseFloat(user.accounts?.[0]?.balance || 0);
};

// Prefixa domínio do ME em caminhos relativos de imagens (ex: /images/shipping-companies/jet.png)
const resolverImagemME = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `https://www.melhorenvio.com.br${path}`;
};

// ─────────────────────────────────────────────
//  COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
const MelhorEnvioDashboard = () => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  // ── Etiquetas
  const [data, setData] = useState({ saldo: 0, etiquetas: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [servicosMap, setServicosMap] = useState({});

  // ── Modais
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure();
  const [selectedEtiqueta, setSelectedEtiqueta] = useState(null);
  const [etiquetaParaCancelar, setEtiquetaParaCancelar] = useState(null);
  const [cancelMotivos, setCancelMotivos] = useState([]);
  const [cancelMotivoId, setCancelMotivoId] = useState('');
  const [cancelDescricao, setCancelDescricao] = useState('');
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelandoId, setCancelandoId] = useState(null);

  // ── Reenvio (abre o formulario de etiqueta da venda, ja em modo reenvio)
  const [vendaReenvio, setVendaReenvio] = useState(null);
  const [carregandoReenvio, setCarregandoReenvio] = useState(null);

  // ── Carrinho
  const [carrinho, setCarrinho] = useState([]);
  const [isLoadingCarrinho, setIsLoadingCarrinho] = useState(false);
  const [isCheckingOutCarrinho, setIsCheckingOutCarrinho] = useState(false);
  const [removendoId, setRemovendoId] = useState(null);

  // ── Conta
  const [usuario, setUsuario] = useState(null);
  const [enderecos, setEnderecos] = useState([]);
  const [isLoadingConta, setIsLoadingConta] = useState(false);
  const [saldoValor, setSaldoValor] = useState('50');
  const [isAdicionandoSaldo, setIsAdicionandoSaldo] = useState(false);
  const [saldoResult, setSaldoResult] = useState(null);

  const toast = useToast();

  // ─────────────────────────────────────────────
  //  ABA 1 – ETIQUETAS
  // ─────────────────────────────────────────────

  const fetchServicos = useCallback(async () => {
    try {
      const res = await api.get('/melhor-envio/servicos');
      if (res.data?.data) {
        const map = {};
        (Array.isArray(res.data.data) ? res.data.data : Object.values(res.data.data)).forEach(s => {
          if (s?.id) map[s.id] = s.name;
        });
        setServicosMap(map);
      }
    } catch (_) {}
  }, []);

  const fetchData = useCallback(async (showGlobalLoader = true) => {
    if (showGlobalLoader) setIsLoading(true);
    setError('');
    try {
      const response = await api.get('/melhor-envio/dashboard', {
        params: { page: currentPage, search: debouncedSearchTerm, status: statusFilter }
      });
      if (response.data.success) {
        setData({ saldo: response.data.saldo, etiquetas: response.data.data });
        setPaginationMeta({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          total: response.data.total
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar o painel do Melhor Envio.');
    } finally {
      if (showGlobalLoader) setIsLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, statusFilter]);

  useEffect(() => { fetchData(); fetchServicos(); }, [fetchData, fetchServicos]);
  useEffect(() => { setCurrentPage(1); }, [debouncedSearchTerm, statusFilter]);

  const getNomeServico = (etiqueta) => {
    if (etiqueta?.servico_id && servicosMap[etiqueta.servico_id]) return servicosMap[etiqueta.servico_id];
    return etiqueta?.nome_servico || `Serviço #${etiqueta?.servico_id || '?'}`;
  };

  // Baixa o arquivo binário da etiqueta (PDF, ZPL ou JPEG)
  const baixarArquivoEtiqueta = async (idExterno, format) => {
    const response = await api.get(`/melhor-envio/imprimir-arquivo`, {
      params: { order: idExterno, format },
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || '';

    // Erro em JSON dentro do blob
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json.error || 'Erro ao baixar etiqueta.');
      } catch {
        throw new Error('Erro ao baixar etiqueta.');
      }
    }

    return response.data;
  };

  const handleBaixarPdf = async (idExterno) => {
    setIsActionLoading(`pdf_${idExterno}`);
    try {
      const blob = await baixarArquivoEtiqueta(idExterno, 'pdf');
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `etiqueta_${idExterno}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({ title: 'PDF baixado com sucesso!', status: 'success', duration: 2500, isClosable: true });
    } catch (err) {
      toast({ title: 'Erro ao baixar PDF', description: err.message || 'Falha ao baixar arquivo.', status: 'error', isClosable: true });
    } finally { setIsActionLoading(null); }
  };

  const handleBaixarZpl = async (idExterno) => {
    setIsActionLoading(`zpl_${idExterno}`);
    try {
      const blob = await baixarArquivoEtiqueta(idExterno, 'zpl');
      const url = URL.createObjectURL(new Blob([blob], { type: 'text/plain' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `etiqueta_${idExterno}.zpl`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({ title: 'ZPL baixado com sucesso!', status: 'success', duration: 2500, isClosable: true });
    } catch (err) {
      toast({ title: 'Erro ao baixar ZPL', description: err.message || 'Falha ao baixar arquivo.', status: 'error', isClosable: true });
    } finally { setIsActionLoading(null); }
  };

  const handleCopiarZpl = async (idExterno) => {
    setIsActionLoading(`copyzpl_${idExterno}`);
    try {
      const blob = await baixarArquivoEtiqueta(idExterno, 'zpl');
      const text = await blob.text();
      await navigator.clipboard.writeText(text);
      toast({ title: 'ZPL copiado!', description: 'Conteúdo copiado para a área de transferência.', status: 'success', duration: 2500, isClosable: true });
    } catch (err) {
      toast({ title: 'Erro ao copiar ZPL', description: err.message || 'Falha ao copiar.', status: 'error', isClosable: true });
    } finally { setIsActionLoading(null); }
  };

  const handleAtualizarRastreio = async (idExterno) => {
    setIsActionLoading(`sync_${idExterno}`);
    try {
      await api.post('/melhor-envio/rastrear', { orders: [idExterno] });
      toast({ title: 'Rastreio atualizado!', status: 'success', isClosable: true, duration: 2500 });
      await fetchData(false);
      if (selectedEtiqueta?.envio_externo_id === idExterno) onClose();
    } catch (err) {
      toast({ title: 'Erro', description: 'Falha ao atualizar rastreio.', status: 'error', isClosable: true });
    } finally { setIsActionLoading(null); }
  };

  const openTimelineModal = (etiqueta) => { setSelectedEtiqueta(etiqueta); onOpen(); };

  const handleAbrirCancelamento = async (etiqueta) => {
    setEtiquetaParaCancelar(etiqueta);
    setCancelMotivoId('');
    setCancelDescricao('');
    setCancelandoId(`check_${etiqueta.envio_externo_id}`);
    try {
      const res = await api.get('/melhor-envio/cancelamento-motivos');
      setCancelMotivos(res.data?.data || []);
    } catch (_) { setCancelMotivos([]); }
    finally { setCancelandoId(null); }
    onCancelOpen();
  };

  const handleCancelar = async () => {
    if (!cancelMotivoId) {
      toast({ title: 'Selecione um motivo para o cancelamento', status: 'warning', isClosable: true });
      return;
    }
    if (!cancelDescricao || cancelDescricao.trim().length < 5) {
      toast({ title: 'Descrição muito curta', description: 'Informe pelo menos 5 caracteres na descrição.', status: 'warning', isClosable: true });
      return;
    }
    setIsCanceling(true);
    try {
      await api.post('/melhor-envio/cancelar', {
        envio_externo_id: etiquetaParaCancelar.envio_externo_id,
        reason_id: cancelMotivoId,
        description: cancelDescricao
      });
      toast({ title: 'Etiqueta cancelada com sucesso!', status: 'success', isClosable: true });
      onCancelClose();
      await fetchData(false);
    } catch (err) {
      toast({ title: 'Erro ao cancelar', description: err.response?.data?.error || 'Falha ao cancelar etiqueta.', status: 'error', isClosable: true, duration: 6000 });
    } finally { setIsCanceling(false); }
  };

  // ── Reenvio: carrega a venda e abre o mesmo formulário do Despacho, já em modo reenvio
  // (medidas preenchidas com as do envio anterior e escolha do frete).
  const handleAbrirReenvio = async (etiqueta) => {
    const termo = etiqueta.venda?.id_origem || String(etiqueta.venda_id);
    setCarregandoReenvio(etiqueta.envio_externo_id);
    try {
      const res = await getVendasML('busca=,status=,data_inicial=,data_final=,mktplaces=,fornecedores=', termo);
      const venda = (res?.vendas || []).find(v => String(v.id_taga) === String(etiqueta.venda_id));

      if (!venda) {
        setCarregandoReenvio(null);
        toast({
          title: 'Venda não encontrada',
          description: `Não consegui carregar a venda ${termo} para abrir o formulário.`,
          status: 'error', isClosable: true
        });
        return;
      }

      setVendaReenvio(venda);
    } catch (err) {
      setCarregandoReenvio(null);
      toast({ title: 'Erro ao abrir o reenvio', description: 'Falha ao carregar a venda.', status: 'error', isClosable: true });
    }
  };

  // O botao da linha segue em loading ate o formulario fechar: o preparo do payload
  // ainda leva alguns segundos depois que a venda carrega.
  const handleFecharReenvio = async () => {
    setVendaReenvio(null);
    setCarregandoReenvio(null);
    await fetchData(false);
  };

  // ─────────────────────────────────────────────
  //  ABA 2 – CARRINHO
  // ─────────────────────────────────────────────

  const fetchCarrinho = useCallback(async () => {
    setIsLoadingCarrinho(true);
    try {
      const res = await api.get('/melhor-envio/carrinho');
      const items = res.data?.data;
      setCarrinho(Array.isArray(items) ? items : (items ? Object.values(items) : []));
    } catch (err) {
      toast({ title: 'Erro ao carregar carrinho', description: err.response?.data?.error || 'Falha na requisição.', status: 'error', isClosable: true });
    } finally { setIsLoadingCarrinho(false); }
  }, [toast]);

  const handleRemoverCarrinho = async (orderId) => {
    setRemovendoId(orderId);
    try {
      await api.delete(`/melhor-envio/carrinho/${orderId}`);
      toast({ title: 'Item removido', status: 'success', isClosable: true, duration: 2500 });
      setCarrinho(prev => prev.filter(i => i.id !== orderId));
    } catch (err) {
      toast({ title: 'Erro ao remover item', description: err.response?.data?.error || 'Falha na requisição.', status: 'error', isClosable: true });
    } finally { setRemovendoId(null); }
  };

  const handleCheckoutCarrinho = async () => {
    if (!carrinho.length) return;
    setIsCheckingOutCarrinho(true);
    try {
      const ids = carrinho.map(i => i.id);
      await api.post('/melhor-envio/checkout', { orders: ids });
      toast({ title: 'Pagamento realizado com sucesso!', description: `${ids.length} etiqueta(s) paga(s).`, status: 'success', isClosable: true });
      fetchCarrinho();
      fetchData(false);
    } catch (err) {
      toast({ title: 'Erro no checkout', description: err.response?.data?.error || 'Falha ao pagar carrinho.', status: 'error', isClosable: true, duration: 6000 });
    } finally { setIsCheckingOutCarrinho(false); }
  };

  const totalCarrinho = carrinho.reduce((sum, i) => sum + parseFloat(i.price || i.price_final || 0), 0);

  // ─────────────────────────────────────────────
  //  ABA 3 – CONTA
  // ─────────────────────────────────────────────

  const fetchConta = useCallback(async () => {
    setIsLoadingConta(true);
    try {
      const [usuarioRes, enderecosRes] = await Promise.all([
        api.get('/melhor-envio/usuario'),
        api.get('/melhor-envio/enderecos'),
      ]);
      setUsuario(usuarioRes.data?.data || usuarioRes.data || null);
      const endData = enderecosRes.data?.data || enderecosRes.data;
      setEnderecos(Array.isArray(endData) ? endData : (endData ? Object.values(endData) : []));
    } catch (err) {
      toast({ title: 'Erro ao carregar dados da conta', description: err.response?.data?.error || 'Falha na requisição.', status: 'error', isClosable: true });
    } finally { setIsLoadingConta(false); }
  }, [toast]);

  const handleAdicionarSaldo = async () => {
    const valor = parseFloat(saldoValor);
    if (!valor || valor < 10) {
      toast({ title: 'Valor mínimo de R$ 10,00', status: 'warning', isClosable: true });
      return;
    }
    setIsAdicionandoSaldo(true);
    setSaldoResult(null);
    try {
      const res = await api.post('/melhor-envio/adicionar-saldo', { value: valor, slug: 'pix' });
      setSaldoResult(res.data?.data || res.data);
      toast({ title: 'Pagamento gerado!', description: 'Siga as instruções na tela para concluir.', status: 'success', isClosable: true });
    } catch (err) {
      toast({ title: 'Erro ao adicionar saldo', description: err.response?.data?.error || 'Falha na operação.', status: 'error', isClosable: true, duration: 6000 });
    } finally { setIsAdicionandoSaldo(false); }
  };

  const handleCopiarPix = (texto) => {
    navigator.clipboard.writeText(texto).then(() => {
      toast({ title: 'Código copiado!', status: 'success', duration: 2000, isClosable: true });
    });
  };

  const handleCopiarCodigo = (codigo) => {
    if (!codigo) return;
    navigator.clipboard.writeText(codigo).then(() => {
      toast({ title: 'Código de rastreio copiado!', description: codigo, status: 'success', duration: 2000, isClosable: true });
    });
  };

  // ─────────────────────────────────────────────
  //  TIMELINE ITEM
  // ─────────────────────────────────────────────
  const TimelineItem = ({ title, date, icon: IconCmp, isAlert = false, isLast = false, isActive = false }) => {
    const done = !!date || isActive;
    const scheme = isAlert ? 'red' : (done ? 'green' : 'gray');
    return (
      <Box position="relative" pb={isLast ? 0 : 7}>
        {!isLast && (
          <Box position="absolute" left="17px" top="36px" bottom={0} w="2px"
            bg={done ? `${scheme}.200` : 'gray.100'} zIndex={0} />
        )}
        <Flex align="flex-start" gap={3} zIndex={1} position="relative">
          <Flex minW="36px" h="36px" borderRadius="full"
            bg={done ? `${scheme}.100` : 'gray.50'} color={done ? `${scheme}.500` : 'gray.300'}
            align="center" justify="center" border="2px solid"
            borderColor={done ? `${scheme}.300` : 'gray.200'} flexShrink={0}>
            <Icon as={IconCmp} boxSize="15px" />
          </Flex>
          <Box pt={1}>
            <Text fontWeight="semibold" fontSize="sm" color={done ? 'gray.800' : 'gray.400'}>{title}</Text>
            <Text fontSize="xs" color={date ? 'gray.500' : 'gray.300'} mt={0.5}>
              {date ? formatDate(date) : 'Aguardando...'}
            </Text>
          </Box>
        </Flex>
      </Box>
    );
  };

  // ─────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────
  return (
    <Box bg="gray.50" minH="100vh">
      <ScrollToTop />

      {/* HEADER */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" px={{ base: 4, md: 8 }} py={4}>
        <Flex align="center" maxW="1300px" mx="auto">
          <Flex align="center" gap={3}>
            <Box bg="blue.600" p={2} borderRadius="lg">
              <Icon as={FaTruck} color="white" boxSize={5} />
            </Box>
            <Box>
              <Flex align="center" gap={2}>
                <Image src="/melhor_envio_logo.webp" alt="Melhor Envio" h="20px" w="auto" objectFit="contain" />
                <Text fontSize="lg" fontWeight="bold" color="gray.800">Painel de Envios</Text>
              </Flex>
              <Text fontSize="xs" color="gray.500">Gerencie etiquetas, carrinho e conta</Text>
            </Box>
          </Flex>
        </Flex>
      </Box>

      <Box maxW="1300px" mx="auto" px={{ base: 4, md: 8 }} py={6}>

        {error && (
          <Alert status="error" mb={5} borderRadius="xl" variant="left-accent">
            <AlertIcon /><AlertTitle mr={2}>Erro</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs colorScheme="blue" variant="soft-rounded" onChange={(idx) => {
          if (idx === 1) fetchCarrinho();
          if (idx === 2) fetchConta();
        }}>
          <Box bg="white" borderRadius="2xl" boxShadow="sm" border="1px solid" borderColor="gray.100" overflow="hidden">
            <Box px={{ base: 4, md: 6 }} pt={4} pb={0} borderBottom="1px solid" borderColor="gray.100">
              <TabList gap={1} pb={4} overflowX="auto">
                <Tab fontWeight="semibold" fontSize="sm" _selected={{ bg: 'blue.600', color: 'white' }}
                  borderRadius="lg" px={4} py={2} flexShrink={0}>
                  <Icon as={FaBoxOpen} mr={2} boxSize={3} />Etiquetas
                  {paginationMeta?.total ? (
                    <Badge ml={2} colorScheme="blue" variant="solid" borderRadius="full" fontSize="0.6rem">
                      {paginationMeta.total}
                    </Badge>
                  ) : null}
                </Tab>
                <Tab fontWeight="semibold" fontSize="sm" _selected={{ bg: 'green.600', color: 'white' }}
                  borderRadius="lg" px={4} py={2} flexShrink={0}>
                  <Icon as={FaShoppingCart} mr={2} boxSize={3} />Carrinho
                  {carrinho.length > 0 && (
                    <Badge ml={2} colorScheme="green" variant="solid" borderRadius="full" fontSize="0.6rem">
                      {carrinho.length}
                    </Badge>
                  )}
                </Tab>
                <Tab fontWeight="semibold" fontSize="sm" _selected={{ bg: 'purple.600', color: 'white' }}
                  borderRadius="lg" px={4} py={2} flexShrink={0}>
                  <Icon as={FaUser} mr={2} boxSize={3} />Conta
                </Tab>
              </TabList>
            </Box>

            <TabPanels>

              {/* ═══════════ ABA 1 — ETIQUETAS ═══════════ */}
              <TabPanel p={{ base: 4, md: 6 }}>

                {/* Stats cards */}
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mb={6}>
                  <Box bg="blue.600" borderRadius="xl" p={5} color="white" position="relative" overflow="hidden">
                    <Box position="absolute" right={-4} top={-4} opacity={0.15}>
                      <Icon as={FaWallet} boxSize={24} />
                    </Box>
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" opacity={0.9}>
                      Saldo Disponível
                    </Text>
                    {isLoading ? (
                      <Skeleton h="40px" mt={2} startColor="blue.500" endColor="blue.700" borderRadius="md" />
                    ) : (
                      <Text fontSize="3xl" fontWeight="black" mt={1}>{formatCurrency(data.saldo)}</Text>
                    )}
                    <Text fontSize="xs" opacity={0.75} mt={1}>Carteira Melhor Envio</Text>
                  </Box>

                  <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={5} position="relative" overflow="hidden">
                    <Box position="absolute" right={-4} top={-4} opacity={0.06}>
                      <Icon as={FaBoxOpen} boxSize={24} color="green.500" />
                    </Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                      Total no Filtro
                    </Text>
                    {isLoading ? (
                      <Skeleton h="40px" mt={2} borderRadius="md" />
                    ) : (
                      <Text fontSize="3xl" fontWeight="black" color="gray.800" mt={1}>{paginationMeta?.total || 0}</Text>
                    )}
                    <Text fontSize="xs" color="gray.400" mt={1}>etiquetas encontradas</Text>
                  </Box>
                </SimpleGrid>

                {/* Filtros */}
                <Flex gap={3} mb={5} direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }}>
                  <InputGroup flex={1} maxW={{ md: '420px' }}>
                    <Input placeholder="Buscar rastreio, cliente, protocolo ou ID..."
                      value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                      bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg"
                      _focus={{ bg: 'white', borderColor: 'blue.400', boxShadow: '0 0 0 3px rgba(66,153,225,0.15)' }}
                    />
                    <InputRightElement><Icon as={FaSearch} color="gray.400" boxSize={3.5} /></InputRightElement>
                  </InputGroup>
                  <Select maxW={{ base: '100%', md: '220px' }} bg="gray.50" borderRadius="lg"
                    borderColor="gray.200" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    _focus={{ borderColor: 'blue.400' }}>
                    <option value="">Todos os Status</option>
                    <option value="pending">Pendente</option>
                    <option value="released">Liberado</option>
                    <option value="received">Na Agência</option>
                    <option value="posted">Em Trânsito</option>
                    <option value="delivered">Entregue</option>
                    <option value="canceled">Cancelado</option>
                    <option value="undelivered">Não Entregue</option>
                  </Select>
                  <Button leftIcon={<FaSyncAlt />} size="md" colorScheme="blue" variant="outline"
                    borderRadius="lg" onClick={fetchData} isLoading={isLoading} flexShrink={0}>
                    {isMobile ? '' : 'Atualizar'}
                  </Button>
                </Flex>

                {/* Tabela / Lista */}
                {isLoading ? (
                  <VStack spacing={3}>
                    {[...Array(5)].map((_, i) => <Skeleton key={i} h="56px" w="full" borderRadius="lg" />)}
                  </VStack>
                ) : data.etiquetas.length === 0 ? (
                  <Flex direction="column" align="center" py={16} color="gray.300">
                    <Icon as={FaBox} boxSize={14} mb={4} />
                    <Text fontSize="lg" fontWeight="semibold" color="gray.500">Nenhuma etiqueta encontrada</Text>
                    <Text fontSize="sm" color="gray.400" mt={1}>Tente ajustar os filtros acima</Text>
                  </Flex>
                ) : (
                  <>
                    {/* Desktop */}
                    <Box display={{ base: 'none', lg: 'block' }} borderRadius="xl" overflow="hidden" border="1px solid" borderColor="gray.200">
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr bg="gray.50">
                            <Th py={3} color="gray.600" fontWeight="bold" fontSize="xs" textTransform="uppercase" letterSpacing="wider">ID / Venda</Th>
                            <Th color="gray.600" fontWeight="bold" fontSize="xs" textTransform="uppercase" letterSpacing="wider">Cliente</Th>
                            <Th color="gray.600" fontWeight="bold" fontSize="xs" textTransform="uppercase" letterSpacing="wider">Status</Th>
                            <Th color="gray.600" fontWeight="bold" fontSize="xs" textTransform="uppercase" letterSpacing="wider">Rastreio / Serviço</Th>
                            <Th color="gray.600" fontWeight="bold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" isNumeric>Custo</Th>
                            <Th color="gray.600" fontWeight="bold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" textAlign="center">Ações</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {data.etiquetas.map((etiqueta) => {
                            const si = getStatusInfo(etiqueta.status);
                            const podeCancelar = canCancel(etiqueta.status);
                            return (
                              <Tr key={etiqueta.id} _hover={{ bg: 'blue.50' }} transition="background 0.15s" cursor="default">
                                <Td py={3} minW="230px">
                                  <Box>
                                    <Box mb={1}>
                                      <Text fontSize="0.6rem" color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight="1">ID Melhor Envio</Text>
                                      <Text fontSize="xs" color="gray.700" fontFamily="mono" fontWeight="semibold" wordBreak="break-all">
                                        {etiqueta.protocolo || etiqueta.envio_externo_id || '—'}
                                      </Text>
                                    </Box>
                                    <Flex gap={3}>
                                      <Box>
                                        <Text fontSize="0.6rem" color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight="1">ID TAGA</Text>
                                        <Text fontSize="xs" color="gray.700" fontWeight="semibold">{etiqueta.venda_id}</Text>
                                      </Box>
                                      <Box>
                                        <Text fontSize="0.6rem" color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight="1">ID Origem</Text>
                                        <Text fontSize="xs" color="blue.600" fontWeight="bold">
                                          {etiqueta.venda?.id_origem ?? etiqueta.venda?.id ?? '—'}
                                        </Text>
                                      </Box>
                                    </Flex>
                                  </Box>
                                </Td>
                                <Td maxW="180px">
                                  <Text fontWeight="medium" color="gray.700" fontSize="sm" noOfLines={2} title={etiqueta.venda?.cliente?.nome || ''}>
                                    {etiqueta.venda?.cliente?.nome || '—'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Box>
                                    {etiqueta.updated_at && (
                                      <Text fontSize="0.6rem" color="gray.400" mb={1} lineHeight="1">
                                        atualizado {formatDate(etiqueta.updated_at)}
                                      </Text>
                                    )}
                                    <Badge colorScheme={si.color} variant="subtle" px={2.5} py={1}
                                      borderRadius="full" fontSize="xs" fontWeight="semibold">
                                      <Flex align="center" gap={1}>
                                        <Icon as={si.icon} boxSize="10px" />
                                        {si.label}
                                      </Flex>
                                    </Badge>
                                  </Box>
                                </Td>
                                <Td minW="200px">
                                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1} noOfLines={1} title={getNomeServico(etiqueta)}>{getNomeServico(etiqueta)}</Text>
                                  {etiqueta.codigo_rastreio ? (
                                    <Flex gap={1} align="center">
                                      <Link href={`https://melhorrastreio.com.br/rastreio/${etiqueta.codigo_rastreio}`} isExternal _hover={{ textDecor: 'none' }} flex={1} minW={0}>
                                        <Flex bg="blue.50" color="blue.700" px={2} py={1} borderRadius="md" align="center" gap={1} _hover={{ bg: 'blue.100' }}>
                                          <Text fontFamily="mono" fontSize="xs" fontWeight="semibold" wordBreak="break-all">
                                            {etiqueta.codigo_rastreio}
                                          </Text>
                                          <Icon as={ExternalLinkIcon} boxSize="9px" flexShrink={0} />
                                        </Flex>
                                      </Link>
                                      <Tooltip label="Copiar código" placement="top" hasArrow>
                                        <IconButton icon={<FaCopy />} size="xs" variant="ghost" colorScheme="gray"
                                          borderRadius="md" aria-label="Copiar rastreio" flexShrink={0}
                                          onClick={() => handleCopiarCodigo(etiqueta.codigo_rastreio)} />
                                      </Tooltip>
                                    </Flex>
                                  ) : (
                                    <Text fontSize="xs" color="gray.300" fontStyle="italic">Sem rastreio</Text>
                                  )}
                                </Td>
                                <Td isNumeric>
                                  <Text fontWeight="bold" color="gray.800" fontSize="sm">
                                    {formatCurrency(etiqueta.preco)}
                                  </Text>
                                </Td>
                                <Td>
                                  <Flex justify="center" gap={1.5}>
                                    <Tooltip label="Linha do Tempo" placement="top" hasArrow>
                                      <IconButton icon={<ViewIcon />} size="sm" colorScheme="teal" variant="ghost"
                                        borderRadius="lg" onClick={() => openTimelineModal(etiqueta)} aria-label="Ver timeline" />
                                    </Tooltip>
                                    <Tooltip label="Sincronizar Rastreio" placement="top" hasArrow>
                                      <IconButton icon={<FaSyncAlt />} size="sm" colorScheme="gray" variant="ghost"
                                        borderRadius="lg" isLoading={isActionLoading === `sync_${etiqueta.envio_externo_id}`}
                                        onClick={() => handleAtualizarRastreio(etiqueta.envio_externo_id)} aria-label="Sincronizar" />
                                    </Tooltip>
                                    <Tooltip label="Baixar PDF" placement="top" hasArrow>
                                      <IconButton icon={<FaFilePdf />} size="sm" colorScheme="blue" borderRadius="lg"
                                        isLoading={isActionLoading === `pdf_${etiqueta.envio_externo_id}`}
                                        isDisabled={etiqueta.status === 'canceled'}
                                        onClick={() => handleBaixarPdf(etiqueta.envio_externo_id)} aria-label="Baixar PDF" />
                                    </Tooltip>
                                    <Tooltip label="Baixar ZPL" placement="top" hasArrow>
                                      <IconButton icon={<FaBarcode />} size="sm" colorScheme="purple" variant="ghost" borderRadius="lg"
                                        isLoading={isActionLoading === `zpl_${etiqueta.envio_externo_id}`}
                                        isDisabled={etiqueta.status === 'canceled'}
                                        onClick={() => handleBaixarZpl(etiqueta.envio_externo_id)} aria-label="Baixar ZPL" />
                                    </Tooltip>
                                    <Tooltip label="Copiar ZPL" placement="top" hasArrow>
                                      <IconButton icon={<FaCopy />} size="sm" colorScheme="purple" variant="ghost" borderRadius="lg"
                                        isLoading={isActionLoading === `copyzpl_${etiqueta.envio_externo_id}`}
                                        isDisabled={etiqueta.status === 'canceled'}
                                        onClick={() => handleCopiarZpl(etiqueta.envio_externo_id)} aria-label="Copiar ZPL" />
                                    </Tooltip>
                                    <Tooltip label={podeCancelar ? 'Cancelar Etiqueta' : `Não cancelável (${si.label})`} placement="top" hasArrow>
                                      <IconButton icon={<FaBan />} size="sm" colorScheme="red" variant="ghost"
                                        borderRadius="lg" isDisabled={!podeCancelar}
                                        isLoading={cancelandoId === `check_${etiqueta.envio_externo_id}`}
                                        onClick={() => podeCancelar && handleAbrirCancelamento(etiqueta)} aria-label="Cancelar" />
                                    </Tooltip>
                                    <Tooltip label="Criar nova etiqueta (reenvio)" placement="top" hasArrow>
                                      <IconButton icon={<FaTruck />} size="sm" colorScheme="orange"
                                        variant={etiqueta.status === 'undelivered' ? 'solid' : 'ghost'}
                                        borderRadius="lg" isDisabled={etiqueta.status === 'pending'}
                                        isLoading={carregandoReenvio === etiqueta.envio_externo_id}
                                        onClick={() => handleAbrirReenvio(etiqueta)} aria-label="Criar nova etiqueta" />
                                    </Tooltip>
                                  </Flex>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>

                    {/* Mobile / Tablet */}
                    <VStack display={{ base: 'flex', lg: 'none' }} spacing={3} align="stretch">
                      {data.etiquetas.map((etiqueta) => {
                        const si = getStatusInfo(etiqueta.status);
                        const podeCancelar = canCancel(etiqueta.status);
                        return (
                          <Box key={etiqueta.id} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                            p={4} boxShadow="xs" _hover={{ boxShadow: 'sm' }} transition="box-shadow 0.15s">
                            {/* Header: status + data */}
                            <Flex justify="space-between" align="center" mb={3} gap={2}>
                              <Badge colorScheme={si.color} variant="subtle" px={2.5} py={1} borderRadius="full" fontSize="xs" flexShrink={0}>
                                <Flex align="center" gap={1}><Icon as={si.icon} boxSize="10px" />{si.label}</Flex>
                              </Badge>
                              {etiqueta.updated_at && (
                                <Text fontSize="0.6rem" color="gray.400" textAlign="right" noOfLines={1}>
                                  atualizado {formatDate(etiqueta.updated_at)}
                                </Text>
                              )}
                            </Flex>

                            {/* IDs sempre visíveis */}
                            <Box mb={3} bg="gray.50" p={2.5} borderRadius="lg">
                              <Box mb={1}>
                                <Text fontSize="0.55rem" color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight="1">ID Melhor Envio</Text>
                                <Text fontSize="xs" color="gray.700" fontFamily="mono" fontWeight="semibold" wordBreak="break-all">
                                  {etiqueta.protocolo || etiqueta.envio_externo_id || '—'}
                                </Text>
                              </Box>
                              <Flex gap={4}>
                                <Box>
                                  <Text fontSize="0.55rem" color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight="1">ID TAGA</Text>
                                  <Text fontSize="xs" color="gray.700" fontWeight="semibold">{etiqueta.venda_id}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="0.55rem" color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight="1">ID Origem</Text>
                                  <Text fontSize="xs" color="blue.600" fontWeight="bold">
                                    {etiqueta.venda?.id_origem || etiqueta.venda?.id || '—'}
                                  </Text>
                                </Box>
                              </Flex>
                            </Box>

                            <Flex align="center" gap={2} mb={3}>
                              <Icon as={FaUser} color="gray.400" boxSize={3} flexShrink={0} />
                              <Text fontWeight="medium" color="gray.700" fontSize="sm" noOfLines={1} title={etiqueta.venda?.cliente?.nome || ''}>
                                {etiqueta.venda?.cliente?.nome || 'Não informado'}
                              </Text>
                            </Flex>

                            <Box mb={3}>
                              <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase" mb={1} noOfLines={1} title={getNomeServico(etiqueta)}>
                                {getNomeServico(etiqueta)}
                              </Text>
                              {etiqueta.codigo_rastreio ? (
                                <Flex gap={1} align="center">
                                  <Link href={`https://melhorrastreio.com.br/rastreio/${etiqueta.codigo_rastreio}`} isExternal _hover={{ textDecor: 'none' }} flex={1} minW={0}>
                                    <Flex bg="blue.50" color="blue.700" px={2} py={1} borderRadius="md" align="center" gap={1} _hover={{ bg: 'blue.100' }}>
                                      <Text fontFamily="mono" fontSize="xs" fontWeight="semibold" wordBreak="break-all">
                                        {etiqueta.codigo_rastreio}
                                      </Text>
                                      <Icon as={ExternalLinkIcon} boxSize="9px" flexShrink={0} />
                                    </Flex>
                                  </Link>
                                  <IconButton icon={<FaCopy />} size="xs" variant="ghost" colorScheme="gray"
                                    borderRadius="md" aria-label="Copiar rastreio" flexShrink={0}
                                    onClick={() => handleCopiarCodigo(etiqueta.codigo_rastreio)} />
                                </Flex>
                              ) : (
                                <Text fontSize="xs" color="gray.300" fontStyle="italic">Sem rastreio</Text>
                              )}
                            </Box>

                            <Flex justify="flex-end" mb={3}>
                              <Box textAlign="right">
                                <Text fontSize="0.6rem" color="gray.400" fontWeight="bold" textTransform="uppercase">Custo</Text>
                                <Text fontWeight="bold" color="gray.800" fontSize="md" lineHeight="1">{formatCurrency(etiqueta.preco)}</Text>
                              </Box>
                            </Flex>
                            <Flex gap={2} justify="flex-end">
                              <IconButton icon={<ViewIcon />} size="sm" colorScheme="teal" variant="ghost" borderRadius="lg"
                                onClick={() => openTimelineModal(etiqueta)} aria-label="Timeline" />
                              <IconButton icon={<FaSyncAlt />} size="sm" colorScheme="gray" variant="ghost" borderRadius="lg"
                                isLoading={isActionLoading === `sync_${etiqueta.envio_externo_id}`}
                                onClick={() => handleAtualizarRastreio(etiqueta.envio_externo_id)} aria-label="Sincronizar" />
                              <IconButton icon={<FaFilePdf />} size="sm" colorScheme="blue" borderRadius="lg"
                                isDisabled={etiqueta.status === 'canceled'}
                                isLoading={isActionLoading === `pdf_${etiqueta.envio_externo_id}`}
                                onClick={() => handleBaixarPdf(etiqueta.envio_externo_id)} aria-label="Baixar PDF" />
                              <IconButton icon={<FaBarcode />} size="sm" colorScheme="purple" variant="ghost" borderRadius="lg"
                                isDisabled={etiqueta.status === 'canceled'}
                                isLoading={isActionLoading === `zpl_${etiqueta.envio_externo_id}`}
                                onClick={() => handleBaixarZpl(etiqueta.envio_externo_id)} aria-label="Baixar ZPL" />
                              <IconButton icon={<FaCopy />} size="sm" colorScheme="purple" variant="ghost" borderRadius="lg"
                                isDisabled={etiqueta.status === 'canceled'}
                                isLoading={isActionLoading === `copyzpl_${etiqueta.envio_externo_id}`}
                                onClick={() => handleCopiarZpl(etiqueta.envio_externo_id)} aria-label="Copiar ZPL" />
                              <Tooltip label={!podeCancelar ? `Não cancelável (${si.label})` : ''} placement="top" hasArrow>
                                <IconButton icon={<FaBan />} size="sm" colorScheme="red" variant="ghost" borderRadius="lg"
                                  isDisabled={!podeCancelar}
                                  isLoading={cancelandoId === `check_${etiqueta.envio_externo_id}`}
                                  onClick={() => podeCancelar && handleAbrirCancelamento(etiqueta)} aria-label="Cancelar" />
                              </Tooltip>
                              <Tooltip label="Criar nova etiqueta (reenvio)" placement="top" hasArrow>
                                <IconButton icon={<FaTruck />} size="sm" colorScheme="orange" borderRadius="lg"
                                  variant={etiqueta.status === 'undelivered' ? 'solid' : 'ghost'}
                                  isDisabled={etiqueta.status === 'pending'}
                                  isLoading={carregandoReenvio === etiqueta.envio_externo_id}
                                  onClick={() => handleAbrirReenvio(etiqueta)} aria-label="Criar nova etiqueta" />
                              </Tooltip>
                            </Flex>
                          </Box>
                        );
                      })}
                    </VStack>
                  </>
                )}

                {/* Paginação */}
                {paginationMeta && paginationMeta.last_page > 1 && (
                  <Flex justify="space-between" align="center" mt={5} pt={4} borderTop="1px solid" borderColor="gray.100">
                    <Button size="sm" leftIcon={<FaChevronLeft />} variant="outline" borderRadius="lg"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      isDisabled={currentPage === 1 || isLoading}>
                      Anterior
                    </Button>
                    <HStack spacing={1}>
                      {[...Array(Math.min(paginationMeta.last_page, 7))].map((_, i) => {
                        const page = i + 1;
                        return (
                          <Button key={page} size="sm" variant={currentPage === page ? 'solid' : 'ghost'}
                            colorScheme={currentPage === page ? 'blue' : 'gray'} borderRadius="lg"
                            onClick={() => setCurrentPage(page)} isDisabled={isLoading} minW="36px">
                            {page}
                          </Button>
                        );
                      })}
                      {paginationMeta.last_page > 7 && (
                        <Text fontSize="sm" color="gray.500">... {paginationMeta.last_page}</Text>
                      )}
                    </HStack>
                    <Button size="sm" rightIcon={<FaChevronRight />} variant="outline" borderRadius="lg"
                      onClick={() => setCurrentPage(p => Math.min(paginationMeta.last_page, p + 1))}
                      isDisabled={currentPage === paginationMeta.last_page || isLoading}>
                      Próximo
                    </Button>
                  </Flex>
                )}
              </TabPanel>

              {/* ═══════════ ABA 2 — CARRINHO ═══════════ */}
              <TabPanel p={{ base: 4, md: 6 }}>
                <Flex justify="space-between" align="center" mb={5}>
                  <Box>
                    <Heading size="md" color="gray.800">Carrinho Melhor Envio</Heading>
                    <Text fontSize="sm" color="gray.500" mt={0.5}>
                      {carrinho.length} {carrinho.length === 1 ? 'item aguardando' : 'itens aguardando'} pagamento
                    </Text>
                  </Box>
                  <Button leftIcon={<FaSyncAlt />} size="sm" variant="outline" colorScheme="green"
                    borderRadius="lg" onClick={fetchCarrinho} isLoading={isLoadingCarrinho}>
                    Atualizar
                  </Button>
                </Flex>

                {isLoadingCarrinho ? (
                  <VStack spacing={3}>{[1,2,3].map(i => <Skeleton key={i} h="80px" borderRadius="xl" w="full" />)}</VStack>
                ) : carrinho.length === 0 ? (
                  <Flex direction="column" align="center" py={16} color="gray.300">
                    <Icon as={FaShoppingCart} boxSize={14} mb={4} />
                    <Text fontSize="lg" fontWeight="semibold" color="gray.500">Carrinho vazio</Text>
                    <Text fontSize="sm" color="gray.400" mt={1}>Use "Adicionar ao Carrinho" nas cotações de frete</Text>
                  </Flex>
                ) : (
                  <>
                    <VStack spacing={3} align="stretch" mb={5}>
                      {carrinho.map((item) => {
                        const serviceName = item.service?.name || item.service_name || 'Serviço';
                        const companyName = item.service?.company?.name || '';
                        const price = parseFloat(item.price || item.price_final || 0);
                        const desconto = parseFloat(item.discount || 0);
                        const precoOriginal = price + desconto;
                        const picUrl = resolverImagemME(item.service?.company?.picture);
                        return (
                          <Box key={item.id} bg="white" border="1px solid" borderColor="gray.200"
                            borderRadius="xl" p={4} _hover={{ borderColor: 'green.300', boxShadow: 'sm' }} transition="all 0.15s">
                            <Flex justify="space-between" align="flex-start" gap={3}>
                              <Flex align="center" gap={3} flex={1} minW={0}>
                                {picUrl && (
                                  <Box bg="white" border="1px solid" borderColor="gray.100" p={1.5} borderRadius="lg" flexShrink={0}>
                                    <Image src={picUrl} alt={companyName} w="48px" h="28px" objectFit="contain"
                                      fallback={<Box w="48px" h="28px" bg="gray.100" borderRadius="sm" />} />
                                  </Box>
                                )}
                                <Box flex={1} minW={0}>
                                  <Flex gap={2} align="baseline" flexWrap="wrap">
                                    <Text fontWeight="bold" color="gray.800" fontSize="sm" noOfLines={1}>{serviceName}</Text>
                                    {companyName && <Text fontSize="xs" color="gray.500" noOfLines={1}>· {companyName}</Text>}
                                  </Flex>
                                  <Flex gap={2} mt={1.5} flexWrap="wrap">
                                    {item.protocol && (
                                      <Tag size="sm" colorScheme="gray" variant="subtle" borderRadius="full">
                                        <TagLabel fontSize="xs" fontFamily="mono">{item.protocol}</TagLabel>
                                      </Tag>
                                    )}
                                    {item.to?.name && (
                                      <Tag size="sm" colorScheme="blue" variant="subtle" borderRadius="full">
                                        <TagLeftIcon as={FaUser} boxSize="9px" />
                                        <TagLabel fontSize="xs" noOfLines={1}>{item.to.name}</TagLabel>
                                      </Tag>
                                    )}
                                    {item.service?.delivery_time && (
                                      <Tag size="sm" colorScheme="purple" variant="subtle" borderRadius="full">
                                        <TagLeftIcon as={FaClock} boxSize="9px" />
                                        <TagLabel fontSize="xs">{item.service.delivery_time} dias</TagLabel>
                                      </Tag>
                                    )}
                                  </Flex>
                                </Box>
                              </Flex>
                              <Flex align="center" gap={2} flexShrink={0}>
                                <Box textAlign="right">
                                  <Text fontWeight="black" color="green.600" fontSize="lg" lineHeight="1">{formatCurrency(price)}</Text>
                                  {desconto > 0 && (
                                    <Text fontSize="xs" color="gray.400" textDecoration="line-through" mt={0.5}>
                                      {formatCurrency(precoOriginal)}
                                    </Text>
                                  )}
                                </Box>
                                <Tooltip label="Remover do carrinho" placement="top" hasArrow>
                                  <IconButton icon={<FaTrash />} size="sm" colorScheme="red" variant="ghost"
                                    borderRadius="lg" isLoading={removendoId === item.id}
                                    onClick={() => handleRemoverCarrinho(item.id)} aria-label="Remover" />
                                </Tooltip>
                              </Flex>
                            </Flex>
                          </Box>
                        );
                      })}
                    </VStack>

                    {/* Total + Checkout */}
                    <Box bg="green.600" borderRadius="xl" p={5} color="white" position="relative" overflow="hidden">
                      <Box position="absolute" right={-6} top={-6} opacity={0.1}>
                        <Icon as={FaMoneyBillWave} boxSize={32} />
                      </Box>
                      <Flex justify="space-between" align="center" direction={{ base: 'column', sm: 'row' }} gap={4}>
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold" opacity={0.85}>Total do Carrinho</Text>
                          <Text fontSize="3xl" fontWeight="black">{formatCurrency(totalCarrinho)}</Text>
                          <Text fontSize="xs" opacity={0.7}>{carrinho.length} item(ns) · será cobrado do saldo ME</Text>
                        </Box>
                        <Button colorScheme="white" color="green.700" bg="white" size="lg" borderRadius="xl"
                          leftIcon={<FaBolt />} isLoading={isCheckingOutCarrinho} onClick={handleCheckoutCarrinho}
                          fontWeight="bold" flexShrink={0} _hover={{ bg: 'green.50' }}>
                          Pagar Tudo Agora
                        </Button>
                      </Flex>
                    </Box>
                  </>
                )}
              </TabPanel>

              {/* ═══════════ ABA 3 — CONTA ═══════════ */}
              <TabPanel p={{ base: 4, md: 6 }}>
                <Flex justify="space-between" align="center" mb={5}>
                  <Box>
                    <Heading size="md" color="gray.800">Dados da Conta</Heading>
                    <Text fontSize="sm" color="gray.500" mt={0.5}>Informações do perfil Melhor Envio</Text>
                  </Box>
                  <Button leftIcon={<FaSyncAlt />} size="sm" variant="outline" colorScheme="purple"
                    borderRadius="lg" onClick={fetchConta} isLoading={isLoadingConta}>
                    Atualizar
                  </Button>
                </Flex>

                {isLoadingConta ? (
                  <VStack spacing={4}>{[1,2,3,4].map(i => <Skeleton key={i} h="80px" borderRadius="xl" w="full" />)}</VStack>
                ) : (
                  <VStack spacing={5} align="stretch">

                    {/* Perfil do usuário */}
                    {usuario && (() => {
                      const telefone = getTelefone(usuario);
                      const saldoUser = getSaldoUsuario(usuario);
                      const conta = usuario.accounts?.[0];
                      const isPj = usuario.document_type === 'cnpj';
                      return (
                      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="2xl" overflow="hidden" position="relative">
                        {/* Faixa decorativa roxa no topo */}
                        <Box bg="purple.600" h="90px" />

                        {/* Avatar flutuante sobre a faixa */}
                        <Avatar
                          name={`${usuario.firstname || ''} ${usuario.lastname || ''}`}
                          src={usuario.picture || usuario.thumbnail}
                          size="xl"
                          border="4px solid white"
                          bg="purple.200" color="purple.700"
                          position="absolute"
                          top="40px"
                          left={{ base: '50%', md: '24px' }}
                          transform={{ base: 'translateX(-50%)', md: 'none' }}
                          boxShadow="md"
                        />

                        {/* Área branca de conteúdo — começa ABAIXO da faixa roxa */}
                        <Box
                          px={{ base: 4, md: 6 }}
                          pt={{ base: '64px', md: 4 }}
                          pb={5}
                        >
                          {/* Header de identidade: espaço reservado para o avatar à esquerda no desktop */}
                          <Box
                            textAlign={{ base: 'center', md: 'left' }}
                            mb={4}
                            pl={{ base: 0, md: '150px' }}
                            minH={{ md: '90px' }}
                          >
                            <Text fontWeight="bold" fontSize="xl" color="gray.800" lineHeight="shorter">
                              {usuario.name || `${usuario.firstname || ''} ${usuario.lastname || ''}`.trim()}
                            </Text>
                            <Text fontSize="sm" color="gray.500" mt={0.5} wordBreak="break-word">{usuario.email}</Text>
                            {usuario.protocol && (
                              <Text fontSize="xs" color="gray.400" fontFamily="mono" mt={0.5}>ID: {usuario.protocol}</Text>
                            )}
                            <Flex
                              gap={2} mt={3} flexWrap="wrap"
                              justify={{ base: 'center', md: 'flex-start' }}
                            >
                              <Badge colorScheme="purple" variant="subtle" borderRadius="full" px={2.5} py={0.5} fontSize="xs">
                                {isPj ? 'CNPJ' : 'CPF'}
                              </Badge>
                              {usuario.email_confirmed_at && (
                                <Tooltip label={`E-mail confirmado em ${formatDate(usuario.email_confirmed_at)}`} hasArrow>
                                  <Badge colorScheme="green" borderRadius="full" px={2.5} py={0.5} display="inline-flex" alignItems="center" gap={1} cursor="help">
                                    <Icon as={FaCheckCircle} boxSize="10px" />
                                    <Text fontSize="xs">E-mail OK</Text>
                                  </Badge>
                                </Tooltip>
                              )}
                              {usuario.status?.status && (
                                <Badge colorScheme={usuario.status.status === 'Allowed' ? 'green' : 'orange'} variant="subtle" borderRadius="full" px={2.5} py={0.5} fontSize="xs">
                                  {usuario.status.status}
                                </Badge>
                              )}
                              {usuario.settings?.two_factor_enabled && (
                                <Tooltip label="Autenticação de dois fatores ativada" hasArrow>
                                  <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2.5} py={0.5} display="inline-flex" alignItems="center" gap={1}>
                                    <Icon as={FaShieldAlt} boxSize="10px" />
                                    <Text fontSize="xs">2FA</Text>
                                  </Badge>
                                </Tooltip>
                              )}
                            </Flex>
                          </Box>

                          <Divider mb={4} />

                          {/* Saldo + limites em destaque */}
                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={4}>
                            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="xl" p={4}>
                              <Flex align="center" gap={3}>
                                <Box bg="green.600" p={2.5} borderRadius="lg" flexShrink={0}>
                                  <Icon as={FaWallet} color="white" boxSize={5} />
                                </Box>
                                <Box minW={0}>
                                  <Text fontSize="xs" color="green.600" fontWeight="bold" textTransform="uppercase">Saldo</Text>
                                  <Text fontSize="xl" fontWeight="black" color="green.700" lineHeight="1.1">{formatCurrency(saldoUser)}</Text>
                                  {conta?.reserved > 0 && (
                                    <Text fontSize="0.65rem" color="gray.500">Reservado: {formatCurrency(conta.reserved)}</Text>
                                  )}
                                </Box>
                              </Flex>
                            </Box>
                            {usuario.limits && (
                              <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="xl" p={4}>
                                <Flex align="center" gap={3}>
                                  <Box bg="blue.600" p={2.5} borderRadius="lg" flexShrink={0}>
                                    <Icon as={FaBoxOpen} color="white" boxSize={5} />
                                  </Box>
                                  <Box minW={0}>
                                    <Text fontSize="xs" color="blue.600" fontWeight="bold" textTransform="uppercase">Envios Ativos</Text>
                                    <Text fontSize="xl" fontWeight="black" color="blue.700" lineHeight="1.1">
                                      {usuario.limits.active_shipments ?? 0}
                                      <Text as="span" fontSize="sm" color="blue.400" fontWeight="normal"> / {usuario.limits.shipments ?? '∞'}</Text>
                                    </Text>
                                    {usuario.limits.shipments_available != null && (
                                      <Text fontSize="0.65rem" color="gray.500">{usuario.limits.shipments_available} disponíveis</Text>
                                    )}
                                  </Box>
                                </Flex>
                              </Box>
                            )}
                            {conta?.limit > 0 && (
                              <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="xl" p={4}>
                                <Flex align="center" gap={3}>
                                  <Box bg="purple.600" p={2.5} borderRadius="lg" flexShrink={0}>
                                    <Icon as={FaBolt} color="white" boxSize={5} />
                                  </Box>
                                  <Box minW={0}>
                                    <Text fontSize="xs" color="purple.600" fontWeight="bold" textTransform="uppercase">Crédito</Text>
                                    <Text fontSize="xl" fontWeight="black" color="purple.700" lineHeight="1.1">{formatCurrency(conta.available_limit)}</Text>
                                    <Text fontSize="0.65rem" color="gray.500">de {formatCurrency(conta.limit)}</Text>
                                  </Box>
                                </Flex>
                              </Box>
                            )}
                          </SimpleGrid>

                          {/* Dados pessoais em grid */}
                          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={3}>
                            {usuario.document && (
                              <Flex align="center" gap={3} p={3} bg="gray.50" borderRadius="xl">
                                <Box bg="white" p={2} borderRadius="lg" border="1px solid" borderColor="gray.200" flexShrink={0}>
                                  <Icon as={FaIdCard} color="blue.500" boxSize={4} />
                                </Box>
                                <Box minW={0}>
                                  <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">{isPj ? 'CNPJ' : 'CPF'}</Text>
                                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" noOfLines={1}>{formatDoc(usuario.document)}</Text>
                                </Box>
                              </Flex>
                            )}
                            {telefone && (
                              <Flex align="center" gap={3} p={3} bg="gray.50" borderRadius="xl">
                                <Box bg="white" p={2} borderRadius="lg" border="1px solid" borderColor="gray.200" flexShrink={0}>
                                  <Icon as={FaPhone} color="teal.500" boxSize={4} />
                                </Box>
                                <Box minW={0}>
                                  <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Telefone</Text>
                                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" noOfLines={1}>{telefone}</Text>
                                </Box>
                              </Flex>
                            )}
                            {usuario.created_at && (
                              <Flex align="center" gap={3} p={3} bg="gray.50" borderRadius="xl">
                                <Box bg="white" p={2} borderRadius="lg" border="1px solid" borderColor="gray.200" flexShrink={0}>
                                  <Icon as={FaCalendarAlt} color="orange.500" boxSize={4} />
                                </Box>
                                <Box minW={0}>
                                  <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Membro desde</Text>
                                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                    {new Date(usuario.created_at).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                                  </Text>
                                </Box>
                              </Flex>
                            )}
                            {usuario.access_at && (
                              <Flex align="center" gap={3} p={3} bg="gray.50" borderRadius="xl">
                                <Box bg="white" p={2} borderRadius="lg" border="1px solid" borderColor="gray.200" flexShrink={0}>
                                  <Icon as={FaClock} color="cyan.500" boxSize={4} />
                                </Box>
                                <Box minW={0}>
                                  <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Último acesso</Text>
                                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">{formatDate(usuario.access_at)}</Text>
                                </Box>
                              </Flex>
                            )}
                            <Flex align="center" gap={3} p={3} bg="gray.50" borderRadius="xl">
                              <Box bg="white" p={2} borderRadius="lg" border="1px solid" borderColor="gray.200" flexShrink={0}>
                                <Icon as={FaEnvelope} color="purple.500" boxSize={4} />
                              </Box>
                              <Box minW={0}>
                                <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">E-mail</Text>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.700" noOfLines={1}>{usuario.email}</Text>
                              </Box>
                            </Flex>
                            {usuario.post_paid && (
                              <Flex align="center" gap={3} p={3} bg="gray.50" borderRadius="xl">
                                <Box bg="white" p={2} borderRadius="lg" border="1px solid" borderColor="gray.200" flexShrink={0}>
                                  <Icon as={FaMoneyBillWave} color="green.500" boxSize={4} />
                                </Box>
                                <Box minW={0}>
                                  <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Modalidade</Text>
                                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                    {usuario.post_paid.is_post_paid ? 'Pós-pago' : 'Pré-pago'}
                                  </Text>
                                </Box>
                              </Flex>
                            )}
                          </SimpleGrid>

                          {/* Roles / certificações */}
                          {Array.isArray(usuario.roles) && usuario.roles.length > 0 && (
                            <Box mt={4}>
                              <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={2}>Certificações da Conta</Text>
                              <Flex gap={2} flexWrap="wrap">
                                {usuario.roles.map((r) => (
                                  <Badge key={r.id} colorScheme="green" variant="subtle" borderRadius="full" px={3} py={1} display="flex" alignItems="center" gap={1}>
                                    <Icon as={FaCheckCircle} boxSize="10px" />
                                    <Text fontSize="xs">{r.name || r.role}</Text>
                                  </Badge>
                                ))}
                              </Flex>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      );
                    })()}

                    {/* Endereços */}
                    {enderecos.length > 0 && (
                      <Box>
                        <Flex align="center" gap={2} mb={3}>
                          <Icon as={FaMapMarkerAlt} color="orange.500" />
                          <Heading size="sm" color="gray.800">Endereços Cadastrados</Heading>
                          <Badge colorScheme="orange" borderRadius="full">{enderecos.length}</Badge>
                        </Flex>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                          {enderecos.map((end, idx) => {
                            const cidade = getCidade(end);
                            const uf = getUF(end);
                            return (
                            <Box key={end.id || idx} bg="white" border="1px solid" borderColor="gray.200"
                              borderRadius="xl" p={4} _hover={{ borderColor: 'orange.300', boxShadow: 'xs' }} transition="all 0.15s">
                              <Flex align="center" gap={2} mb={2} flexWrap="wrap">
                                <Box bg="orange.100" p={1.5} borderRadius="lg">
                                  <Icon as={FaMapMarkerAlt} color="orange.500" boxSize={3.5} />
                                </Box>
                                <Text fontWeight="bold" color="gray.800" fontSize="sm">
                                  {end.label || end.alias || end.description || `Endereço ${idx + 1}`}
                                </Text>
                                {end.confirmed_at && (
                                  <Badge colorScheme="green" variant="subtle" borderRadius="full" fontSize="xs">Confirmado</Badge>
                                )}
                                {end.is_default && (
                                  <Badge colorScheme="blue" variant="subtle" borderRadius="full" fontSize="xs">Principal</Badge>
                                )}
                              </Flex>
                              <Text fontSize="sm" color="gray.700">
                                {end.address}{end.number ? `, ${end.number}` : ''}{end.complement ? ` - ${end.complement}` : ''}
                              </Text>
                              <Text fontSize="xs" color="gray.500" mt={0.5}>
                                {end.district && `${end.district} · `}{cidade}{uf ? `/${uf}` : ''}
                              </Text>
                              <Flex align="center" gap={1} mt={1}>
                                <Icon as={FaMapMarkerAlt} color="gray.300" boxSize={3} />
                                <Text fontSize="xs" color="gray.400" fontFamily="mono">CEP {end.postal_code}</Text>
                              </Flex>
                            </Box>
                          );})}
                        </SimpleGrid>
                      </Box>
                    )}

                    {/* Adicionar Saldo via PIX */}
                    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="2xl" overflow="hidden">
                      <Box bg="green.600" px={5} py={4}>
                        <Flex align="center" gap={2}>
                          <Icon as={FaQrcode} color="white" boxSize={5} />
                          <Text fontWeight="bold" color="white" fontSize="md">Adicionar Saldo via PIX</Text>
                        </Flex>
                        <Text fontSize="xs" color="green.100" mt={0.5}>Pagamento instantâneo · Mínimo de R$ 10,00</Text>
                      </Box>
                      <Box p={5}>
                        <Flex gap={4} direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'flex-end' }}>
                          <FormControl maxW={{ md: '280px' }}>
                            <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">Valor (R$)</FormLabel>
                            <NumberInput min={10} value={saldoValor} onChange={val => setSaldoValor(val)} borderRadius="lg">
                              <NumberInputField borderRadius="lg" fontSize="lg" fontWeight="bold" h="48px" />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>
                          <Button colorScheme="green" size="lg" leftIcon={<FaQrcode />} borderRadius="xl"
                            isLoading={isAdicionandoSaldo} onClick={handleAdicionarSaldo}
                            fontWeight="bold" h="48px">
                            Gerar QR Code PIX
                          </Button>
                        </Flex>

                        {/* Resultado */}
                        {saldoResult && (() => {
                          // Campos reais retornados pelo ME:
                          // - redirect: URL do QR code (imagem SVG)
                          // - digitable: código PIX copia-e-cola
                          // - payment.link: idem redirect
                          // Parsear o payment.response (JSON string) como fallback
                          let respostaYapay = null;
                          try {
                            respostaYapay = saldoResult.payment?.response ? JSON.parse(saldoResult.payment.response) : null;
                          } catch (_) {}
                          const pagamentoYapay = respostaYapay?.data_response?.transaction?.payment;
                          const qrImageUrl = saldoResult.redirect || saldoResult.payment?.link || pagamentoYapay?.qrcode_path;
                          const qrDigitable = saldoResult.digitable || pagamentoYapay?.qrcode_original_path;
                          const yapayUrl = pagamentoYapay?.url_payment;
                          const valorGerado = saldoResult.payment?.value || parseFloat(saldoValor);

                          return (
                            <Box mt={5} p={{ base: 4, md: 5 }} bg="green.50" border="1px solid" borderColor="green.200" borderRadius="xl">
                              <Flex align="center" gap={2} mb={4}>
                                <Icon as={FaCheckCircle} color="green.500" boxSize={5} />
                                <Text fontWeight="bold" color="green.700">Pagamento PIX Gerado</Text>
                                <Badge colorScheme="yellow" variant="solid" borderRadius="full" ml="auto">
                                  Aguardando pagamento
                                </Badge>
                              </Flex>

                              <Flex direction={{ base: 'column', md: 'row' }} gap={5} align={{ base: 'center', md: 'flex-start' }}>
                                {/* QR Code à esquerda */}
                                {qrImageUrl && (
                                  <Box bg="white" p={3} borderRadius="xl" border="2px solid" borderColor="green.300" boxShadow="sm" flexShrink={0}>
                                    <Image src={qrImageUrl} alt="QR Code PIX" w="220px" h="220px" objectFit="contain" />
                                  </Box>
                                )}

                                {/* Instruções + código à direita */}
                                <Box flex={1} w="full">
                                  <Box mb={3}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>Valor</Text>
                                    <Text fontSize="2xl" fontWeight="black" color="green.700" lineHeight="1">{formatCurrency(valorGerado)}</Text>
                                  </Box>

                                  <Box mb={3}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>Como pagar</Text>
                                    <Text fontSize="sm" color="gray.700" lineHeight="short">
                                      1. Abra o app do seu banco<br />
                                      2. Escolha a opção <b>PIX → Pagar com QR Code</b><br />
                                      3. Escaneie o QR ao lado ou use o código copia e cola abaixo
                                    </Text>
                                  </Box>

                                  {qrDigitable && (
                                    <Box>
                                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1.5}>Código copia e cola</Text>
                                      <Box bg="white" p={2.5} borderRadius="lg" border="1px solid" borderColor="gray.200"
                                        fontFamily="mono" fontSize="xs" color="gray.700" wordBreak="break-all" mb={2}
                                        maxH="80px" overflowY="auto">
                                        {qrDigitable}
                                      </Box>
                                      <Button size="sm" leftIcon={<FaCopy />} colorScheme="green" variant="solid"
                                        borderRadius="lg" onClick={() => handleCopiarPix(qrDigitable)}>
                                        Copiar Código PIX
                                      </Button>
                                    </Box>
                                  )}

                                  {yapayUrl && (
                                    <Button size="sm" mt={3} variant="outline" colorScheme="blue" borderRadius="lg"
                                      leftIcon={<ExternalLinkIcon />}
                                      onClick={() => window.open(yapayUrl, '_blank')}>
                                      Abrir página de pagamento
                                    </Button>
                                  )}
                                </Box>
                              </Flex>

                              <Alert status="info" mt={4} borderRadius="lg" fontSize="xs" variant="left-accent">
                                <AlertIcon />
                                <Box>
                                  <AlertTitle fontSize="xs">Após o pagamento</AlertTitle>
                                  <AlertDescription fontSize="xs">
                                    O saldo aparece automaticamente em sua conta Melhor Envio em até 2 minutos. Clique em "Atualizar" para recarregar.
                                  </AlertDescription>
                                </Box>
                              </Alert>
                            </Box>
                          );
                        })()}
                      </Box>
                    </Box>

                  </VStack>
                )}
              </TabPanel>
            </TabPanels>
          </Box>
        </Tabs>
      </Box>

      {/* ══════════════ MODAL TIMELINE ══════════════ */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.500" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
          <ModalHeader bg="gray.50" borderBottom="1px solid" borderColor="gray.100" py={4}>
            <Flex align="center" gap={2}>
              <Icon as={FaRoute} color="blue.500" />
              <Text color="gray.800" fontSize="lg" fontWeight="bold">Linha do Tempo</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton top={3} />
          <ModalBody py={6} px={6}>
            {selectedEtiqueta && (() => {
              const si = getStatusInfo(selectedEtiqueta.status);
              // Todos os eventos possíveis na ordem cronológica
              const eventos = [
                { key: 'created', title: 'Etiqueta Criada no Carrinho', date: selectedEtiqueta.created_at, icon: TimeIcon },
                { key: 'paid', title: 'Pagamento Aprovado', date: selectedEtiqueta.paid_at, icon: FaWallet },
                { key: 'generated', title: 'Etiqueta Gerada (PDF)', date: selectedEtiqueta.generated_at, icon: FaFilePdf },
                {
                  key: 'received', title: 'Recebido no Ponto de Coleta',
                  date: selectedEtiqueta.status === 'received' ? selectedEtiqueta.updated_at : null,
                  icon: FaStore,
                  isActive: selectedEtiqueta.status === 'received',
                },
                { key: 'posted', title: 'Postado na Transportadora', date: selectedEtiqueta.posted_at, icon: FaTruck },
                { key: 'delivered', title: 'Entregue ao Destinatário', date: selectedEtiqueta.delivered_at, icon: FaCheck },
              ];
              const eventosFinais = [];
              if (selectedEtiqueta.undelivered_at || selectedEtiqueta.status === 'undelivered') {
                eventosFinais.push({ key: 'undelivered', title: 'Tentativa Sem Sucesso', date: selectedEtiqueta.undelivered_at || selectedEtiqueta.updated_at, icon: FaExclamationCircle, isAlert: true });
              }
              if (selectedEtiqueta.expired_at) {
                eventosFinais.push({ key: 'expired', title: 'Etiqueta Expirada', date: selectedEtiqueta.expired_at, icon: FaClock, isAlert: true });
              }
              if (selectedEtiqueta.canceled_at) {
                eventosFinais.push({ key: 'canceled', title: 'Etiqueta Cancelada', date: selectedEtiqueta.canceled_at, icon: FaTimesCircle, isAlert: true });
              }

              return (
              <VStack align="stretch" spacing={5}>
                <Box bg="blue.50" p={4} borderRadius="xl" border="1px solid" borderColor="blue.100">
                  <Flex justify="space-between" align="flex-start" gap={3} mb={2}>
                    <Box flex={1} minW={0}>
                      <Text fontSize="xs" color="blue.500" fontWeight="bold" textTransform="uppercase" mb={1}>Protocolo / ID Melhor Envio</Text>
                      <Text fontSize="sm" color="gray.800" fontWeight="bold" fontFamily="mono" noOfLines={1}>
                        {selectedEtiqueta.protocolo || selectedEtiqueta.envio_externo_id}
                      </Text>
                    </Box>
                    <Badge colorScheme={si.color} variant="subtle" px={2.5} py={1} borderRadius="full" fontSize="xs" flexShrink={0}>
                      <Flex align="center" gap={1}><Icon as={si.icon} boxSize="10px" />{si.label}</Flex>
                    </Badge>
                  </Flex>
                  <Divider my={2} borderColor="blue.200" />
                  <SimpleGrid columns={2} spacing={2} fontSize="xs">
                    <Box>
                      <Text color="gray.500" fontWeight="semibold">Serviço</Text>
                      <Text color="gray.700">{getNomeServico(selectedEtiqueta)}</Text>
                    </Box>
                    <Box>
                      <Text color="gray.500" fontWeight="semibold">Custo</Text>
                      <Text color="gray.700" fontWeight="bold">{formatCurrency(selectedEtiqueta.preco)}</Text>
                    </Box>
                    {selectedEtiqueta.venda?.cliente?.nome && (
                      <Box gridColumn="1 / -1">
                        <Text color="gray.500" fontWeight="semibold">Destinatário</Text>
                        <Text color="gray.700" noOfLines={1}>{selectedEtiqueta.venda.cliente.nome}</Text>
                      </Box>
                    )}
                    {selectedEtiqueta.codigo_rastreio && (
                      <Box gridColumn="1 / -1">
                        <Text color="gray.500" fontWeight="semibold">Código de Rastreio</Text>
                        <Flex align="center" gap={2}>
                          <Text color="blue.600" fontFamily="mono" fontWeight="bold">{selectedEtiqueta.codigo_rastreio}</Text>
                          <IconButton icon={<FaCopy />} size="xs" variant="ghost" aria-label="Copiar"
                            onClick={() => handleCopiarCodigo(selectedEtiqueta.codigo_rastreio)} />
                        </Flex>
                      </Box>
                    )}
                  </SimpleGrid>
                </Box>

                <Box>
                  <Text fontWeight="bold" color="gray.600" mb={4} fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                    Histórico de Eventos
                  </Text>
                  <Box pl={1}>
                    {eventos.map((ev, idx) => {
                      const isLast = idx === eventos.length - 1 && eventosFinais.length === 0;
                      return <TimelineItem key={ev.key} title={ev.title} date={ev.date} icon={ev.icon} isActive={ev.isActive} isLast={isLast} />;
                    })}
                    {eventosFinais.map((ev, idx) => (
                      <TimelineItem key={ev.key} title={ev.title} date={ev.date} icon={ev.icon}
                        isAlert={ev.isAlert} isLast={idx === eventosFinais.length - 1} />
                    ))}
                  </Box>
                </Box>
              </VStack>
              );
            })()}
          </ModalBody>
          <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.100" py={3}>
            <Flex w="full" justify="space-between" align="center">
              {selectedEtiqueta?.codigo_rastreio && (
                <Link href={`https://melhorrastreio.com.br/rastreio/${selectedEtiqueta.codigo_rastreio}`} isExternal>
                  <Button size="sm" variant="ghost" colorScheme="blue" leftIcon={<ExternalLinkIcon />} borderRadius="lg">
                    Rastrear Online
                  </Button>
                </Link>
              )}
              <Button ml="auto" size="sm" variant="outline" colorScheme="gray" borderRadius="lg" onClick={onClose}>Fechar</Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ══════════════ MODAL CANCELAMENTO ══════════════ */}
      <Modal isOpen={isCancelOpen} onClose={onCancelClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.500" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
          <ModalHeader bg="red.50" borderBottom="1px solid" borderColor="red.100" py={4}>
            <Flex align="center" gap={2}>
              <Icon as={FaBan} color="red.500" />
              <Text color="red.700" fontWeight="bold">Cancelar Etiqueta</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton top={3} isDisabled={isCanceling} />
          <ModalBody py={5} px={5}>
            {etiquetaParaCancelar && (
              <VStack spacing={4} align="stretch">
                <Alert status="warning" borderRadius="xl" variant="left-accent">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Esta ação não pode ser desfeita.</AlertTitle>
                    <AlertDescription fontSize="xs">
                      Etiqueta: <strong>{etiquetaParaCancelar.protocolo || etiquetaParaCancelar.envio_externo_id}</strong>
                    </AlertDescription>
                  </Box>
                </Alert>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">Motivo do Cancelamento</FormLabel>
                  <Select placeholder="Selecione um motivo..." value={cancelMotivoId}
                    onChange={e => setCancelMotivoId(e.target.value)} borderRadius="lg"
                    _focus={{ borderColor: 'red.400' }}>
                    {cancelMotivos.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.description || m.reason || m.label || `Motivo ${m.id}`}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                    Descrição
                    <Text as="span" fontSize="xs" color="gray.400" fontWeight="normal" ml={1}>(mínimo 5 caracteres)</Text>
                  </FormLabel>
                  <Textarea
                    placeholder="Descreva o motivo em mais detalhes..."
                    value={cancelDescricao} onChange={e => setCancelDescricao(e.target.value)}
                    rows={3} borderRadius="lg" resize="none"
                    _focus={{ borderColor: 'red.400' }}
                  />
                  <Text fontSize="xs" color={cancelDescricao.length < 5 ? 'red.400' : 'gray.400'} mt={1} textAlign="right">
                    {cancelDescricao.length} caracteres {cancelDescricao.length < 5 ? '(mínimo 5)' : '✓'}
                  </Text>
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2} py={3}>
            <Button variant="ghost" onClick={onCancelClose} isDisabled={isCanceling} borderRadius="lg">
              Voltar
            </Button>
            <Button colorScheme="red" leftIcon={<FaBan />} isLoading={isCanceling}
              isDisabled={!cancelMotivoId || cancelDescricao.trim().length < 5}
              onClick={handleCancelar} borderRadius="lg">
              Confirmar Cancelamento
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reenvio: mesmo formulario do Despacho, ja com as medidas do envio anterior */}
      {vendaReenvio && (
        <GeradorEtiquetaMelhorEnvio
          key={vendaReenvio.id_taga}
          venda={vendaReenvio}
          autoReenvio
          onFechar={handleFecharReenvio}
        />
      )}
    </Box>
  );
};

export default MelhorEnvioDashboard;
